/**
 * Step-level validation logic
 */

import {
    validateName,
    validateEmail,
    validateBirthday,
    validateSex,
    validateCity,
    validateHomeCity,
    validatePassword,
    validateUsername
} from './validators.js';

const FIELD_MAP = ['email', 'name', 'birthday', 'sex', 'currentCity', 'homeCity', 'password', 'username'];

export function getFieldForStep(step) {
    return FIELD_MAP[step - 1];
}

export function isStepValid(step, state) {
    switch (step) {
        case 1:
            return validateEmail(state.email).valid;
        case 2:
            return validateName(state.name).valid;
        case 3:
            return validateBirthday(state.day, state.month, state.year, state.minAge).valid;
        case 4:
            return validateSex(state.sex).valid;
        case 5:
            return validateCity(state.cityId).valid;
        case 6:
            return validateHomeCity(state.homeCityId).valid;
        case 7:
            return validatePassword(state.password).valid;
        case 8:
            return validateUsername(state.username).valid;
        default:
            return true;
    }
}

export function getStepError(step, state) {
    const field = getFieldForStep(step);

    // Don't show errors during initialization
    if (state.isInitializing) {
        return null;
    }

    // Don't show errors until field is touched
    if (!state.touched[field]) {
        return null;
    }

    // Check server errors FIRST (they are authoritative)
    if (state.errors[field]?.message) {
        return state.errors[field].message;
    }
    if (state.errors[field]?.msg) {
        return state.errors[field].msg;
    }
    if (typeof state.errors[field] === 'string') {
        return state.errors[field];
    }

    // If the step is valid, don't show errors (handles case where touched
    // is set during init but data is already valid from localStorage)
    if (isStepValid(step, state)) return null;

    let validationResult;
    switch (step) {
        case 1:
            validationResult = validateEmail(state.email);
            break;
        case 2:
            validationResult = validateName(state.name);
            break;
        case 3:
            validationResult = validateBirthday(state.day, state.month, state.year, state.minAge);
            break;
        case 4:
            validationResult = validateSex(state.sex);
            break;
        case 5:
            validationResult = validateCity(state.cityId);
            break;
        case 6:
            validationResult = validateHomeCity(state.homeCityId);
            break;
        case 7:
            validationResult = validatePassword(state.password);
            break;
        case 8:
            validationResult = validateUsername(state.username);
            break;
        default:
            return null;
    }

    // Show client-side validation error if field is invalid
    if (!validationResult.valid) {
        return validationResult.error;
    }

    return null;
}

export function getFirstInvalidStep(state, totalSteps) {
    for (let step = 1; step <= totalSteps; step++) {
        if (!isStepValid(step, state)) {
            return step;
        }
    }
    return null;
}

export function markStepTouched(step, state) {
    const field = getFieldForStep(step);
    state.touched[field] = true;
}
